import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/contexts/ToastContext'
import { feedService } from '@/services/feedService'
import { PostVisibility } from '@/types/feed'
import type { IPost } from '@/types/feed'

const MAX_POST_LENGTH = 5000

const schema = z.object({
  content: z.string().trim().min(1, 'Write something to share').max(MAX_POST_LENGTH, `Max ${MAX_POST_LENGTH} characters`),
  visibility: z.nativeEnum(PostVisibility)
})

type FormValues = z.infer<typeof schema>

interface PostComposerProps {
  onCreated?: (post: IPost) => void
}

const PostComposer: React.FC<PostComposerProps> = ({ onCreated }) => {
  const { showToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { content: '', visibility: PostVisibility.PUBLIC }
  })

  const content = watch('content') ?? ''
  const charCount = content.length

  const onSubmit = async (values: FormValues): Promise<void> => {
    setIsSubmitting(true)
    try {
      const post = await feedService.createPost({
        content: values.content,
        visibility: values.visibility
      })
      reset({ content: '', visibility: values.visibility })
      onCreated?.(post)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create post'
      showToast({ type: 'error', message })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
    >
      <textarea
        {...register('content')}
        rows={3}
        placeholder="Share an update with your network"
        className="w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      {errors.content && (
        <p className="text-xs text-red-600">{errors.content.message}</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-sm">
          <label className="text-gray-600">Visibility</label>
          <select
            {...register('visibility')}
            className="rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
          >
            <option value={PostVisibility.PUBLIC}>Public</option>
            <option value={PostVisibility.CONNECTIONS}>Connections only</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs ${charCount > MAX_POST_LENGTH ? 'text-red-600' : 'text-gray-400'}`}>
            {charCount}/{MAX_POST_LENGTH}
          </span>
          <Button type="submit" size="sm" isLoading={isSubmitting} disabled={charCount === 0}>
            <Send className="w-4 h-4 mr-1" />
            Post
          </Button>
        </div>
      </div>
    </form>
  )
}

export default PostComposer
